import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import { ZInput } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import { UploadedFile } from "@/shared/types/file-upload";

import { useLoggedInMember } from "@/modules/auth/states";
import { TeamRole } from "@/modules/team/types";
import { addNotification } from "@/shared/states/notification";
import { cn, Select, SelectItem, Switch, Textarea } from "@nextui-org/react";
import { AxiosError } from "axios";
import { cloneDeep } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { simplifyFileTypes } from "../../../../shared/components/custom/file-upload/index";
import {
  AGENT_TYPES,
  GapAssessmentTypes,
  RiskAssessmentTypes,
  UploadDialogState,
} from "../../types/index.ts";
import { helper } from "./helper.ts";
import { checkAlphaNumeric, containsSpecialCharacters, containsSpecialCharactersForTitle, processDataBasedOnAssessment } from "./util.ts";

// Add filetypes from simplifyFileTypes keys.
const DEFAULT_ALLOWED_FILE_TYPES = simplifyFileTypes([]).map(
  (key: string) => key
);

// Define a type for the error messages
type ErrorMessages = {
  session?: string;
  [key: string]: string | undefined; // allows for dynamic keys
};

export default function UploadDialog({
  data,
  setIsOpen,
}: {
  data: UploadDialogState;
  setIsOpen: (data: UploadDialogState) => void;
}) {
  const { isOpen, type } = data;
  const loggedInMember = useLoggedInMember();

  const helperData = useMemo(() => {
    if(type === AGENT_TYPES.RISK_ASSESSMENT){
      const temp = cloneDeep((helper as any)[type as AGENT_TYPES])

      if(temp.dropdown && loggedInMember){
        temp.dropdown.options = temp.dropdown.options.filter((option: any) => {
          if(option.value === 'nist_ai_rmf' && loggedInMember.roles.find(rol => rol.role_id === TeamRole.ZANIA_NIST_AI_RMF)){
            return true
          }
          if(option.value === 'nist-csf-2' && loggedInMember.roles.find(rol => rol.role_id === TeamRole.ZANIA_RISK_ASSESSMENT)){
            return true
          }
          // saurabh update value to your values and role to your value
          if(option.value === RiskAssessmentTypes.FAIR && loggedInMember.roles.find(rol => rol.role_id === TeamRole.ZANIA_FAIR_ASSESSMENT)){
            return true
          }

          return false
        })
      }

      return temp
    }

    return (helper as any)[type as AGENT_TYPES]

  }, [type, loggedInMember]);
  const [isLoading, setIsLoading] = useState(false);
  const [allFiles, setAllFiles] = useState<UploadedFile[]>([]);
  const [errors, setErrors] = useState<ErrorMessages>({});
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<
    string
  >();
  const [isToggleSelected, setIsToggleSelected] = useState(false);
  const fileUploadRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [inputs, setInputs] = useState<{ [key: string]: string | boolean }>({});

  // Get the logged-in member to check permissions
  const member = useLoggedInMember();
  // Check if user has access to FAIR assessment
  const userRoles = member?.roles?.map(({ role_id }) => role_id as TeamRole) || [];
  const hasFairAccess = userRoles.includes(TeamRole.ZANIA_FAIR_ASSESSMENT);

  // Filter dropdown options based on permissions
  const filteredDropdownOptions = useMemo(() => {
    if (!helperData?.dropdown?.options) return [];

    return (helperData.dropdown.options as { value: string; label: string; isDefault: boolean }[]).filter(option => {
      // If it's the FAIR option, only show it if the user has access
      if (option.value === RiskAssessmentTypes.FAIR) {
        return hasFairAccess;
      }
      // Show all other options
      return true;
    });
  }, [helperData?.dropdown?.options, hasFairAccess]);

  const navigate = useNavigate();

  // Combining the framework change and modal open effects into one
  useEffect(() => {
    if (isOpen) {
      // When modal opens, reset everything except session title
      setIsLoading(false);
      setErrors({});
      setInputs(prevInputs => {
        const newInputs = { ...prevInputs };
        // Only preserve the session title
        const sessionTitle = newInputs.session;
        return { session: sessionTitle };
      });
    }
  }, [isOpen]);

  // Separate effect for framework changes
  useEffect(() => {
    if (selectedDropdownOption) {
      setIsToggleSelected(false);
      setAllFiles([]);
      // Reset all inputs except session title
      setInputs(prevInputs => ({
        ...prevInputs,
        risk_statement: '', // Clear risk statement when framework changes
      }));
    }
  }, [selectedDropdownOption]);

  // Set default framework option
  useEffect(() => {
    let defaultOption = helperData?.dropdown?.options.find(
      (option: { isDefault: boolean }) => option.isDefault
    );
    if (!defaultOption && helperData?.dropdown?.options?.[0]) {
      defaultOption = helperData.dropdown.options[0];
    }

    if (
      (type !== AGENT_TYPES.GAP_ASSESSMENT)
    ) {
      setSelectedDropdownOption(defaultOption?.value ?? "");
    }
  }, [helperData?.dropdown?.option, type, isOpen]);

  const buttonDisabled = useMemo(() => {
    // Check for any validation errors
    const hasValidationErrors = Object.values(errors).some(error => error !== "" && error !== undefined);
    
    if (hasValidationErrors) {
      // If there are validation errors, disable the button
      return true;
    }
  
    const areInputsMissing = helperData?.inputs?.some(
      (input: { key: string; isRequired: boolean }) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        input.isRequired &&
        ((inputs[input.key] ?? "") as string).trim().length === 0
    );

    if (areInputsMissing) {
      // Some required inputs are missing.
      return true;
    }

    // Check if risk field is required for FAIR but missing
    if (selectedDropdownOption === RiskAssessmentTypes.FAIR &&
      (!inputs.risk_statement || (inputs.risk_statement as string).trim().length === 0)) {
      return true;
    }

    if (!selectedDropdownOption && type === AGENT_TYPES.GAP_ASSESSMENT) {
      return true;
    }

    // check if the helperData.uploads array is empty
    const shouldHaveFiles = helperData?.uploads.length > 0;
    // For FAIR, files are optional
    if (shouldHaveFiles && allFiles.length === 0 && selectedDropdownOption !== RiskAssessmentTypes.FAIR) {
      // no files uploaded, but the helperData.uploads array is not empty and it's not FAIR
      return true;
    }

    let maxFilesExceeded = false;
    for (const upload of helperData?.uploads ?? []) {
      const filteredAllFiles = allFiles.filter(
        (file) => file.type === upload.type
      );

      if(!filteredAllFiles.length){
        // If multiple fields require file, check number of files attached for all fields type
        return true;
      }

      maxFilesExceeded =
        maxFilesExceeded || filteredAllFiles.length > upload.maxFiles;
    }

    if (maxFilesExceeded) {
      // the number of files exceeds the maximum allowed uploads as defined in the helperData.uploads array
      return true;
    }

    if (!type) {
      // the type is not defined; impossible to proceed
      return true;
    }

    return allFiles.some((file) => file.status !== "success");
  }, [allFiles, type, helperData, inputs, selectedDropdownOption, errors]);

  const handleSubmit = async () => {
    setIsLoading(true);

    let isError = false;
    for (const input of   helperData?.inputs ?? []) {
      if (input?.isRequired && !inputs[input.key]) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "This field is required",
        }));
      }

      // character limit for input
      if(inputs[input.key] && input.type === 'text' && inputs[input.key].toString().length > 25){
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "Input cannot exceed 25 characters",
        }));
      }
      if (input.key === "title") {
        if (containsSpecialCharactersForTitle(inputs[input.key] as string)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: `Input cannot include special characters except '-', '_', or '/'.`
          }));
        }
      } else if (input.key === "vendorName") {
        if (checkAlphaNumeric(inputs[input.key] as string)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: "Special characters are not allowed.",
          }));
        }
      } else if (inputs[input.key] && typeof inputs[input.key] === 'string' && containsSpecialCharacters(inputs[input.key] as string)) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "Special characters are not allowed.",
        }));
      }
    }

    if (isError) {
      setIsLoading(false);
      return;
    }

    try {
      if (helperData?.uploads.length !== 0 && allFiles.length === 0 && selectedDropdownOption !== RiskAssessmentTypes.FAIR) {
        return;
      }
      await processDataBasedOnAssessment(
        type,
        allFiles,
        navigate,
        inputs,
        selectedDropdownOption,
        isToggleSelected
      );
    } catch (error) {
      const errorType = (error as AxiosError<{ error_type: string }>).response
        ?.data.error_type;

      if (errorType === "Validation Error") {
        setErrors({ title: "Session name already exists" });
        return;
      }
      console.error(error);
      addNotification({
        message: "Error processing the uploaded file(s)",
        type: "error",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const handleSetFiles = (newFiles: UploadedFile[], fileType: string) => {
    setAllFiles((prevFiles) => {
      const otherTypeFiles = prevFiles.filter((file) => file.type !== fileType);
      const combinedFiles = [...otherTypeFiles, ...newFiles];
      const uniqueFiles = combinedFiles.reduce((acc, current) => {
        const existingFile = acc.find(
          (item) => item.file.name === current.file.name
        );
        if (!existingFile) {
          return [...acc, current];
        } else {
          return acc.map((item) =>
            item.file.name === current.file.name ? current : item
          );
        }
      }, [] as UploadedFile[]);

      // Scroll to the newly added file
      setTimeout(() => {
        const lastFile = newFiles[newFiles.length - 1];
        if (lastFile && fileUploadRefs.current[fileType]) {
          fileUploadRefs.current[fileType]?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 100);

      return uniqueFiles;
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    type: string
  ) => {
    const newValue = e.target.value.trimStart();

    // Clear existing error for this field
    setErrors({ ...errors, [key]: "" });

    // Skip validation if field is empty (except for required fields, which are validated on submit)
    if (!newValue) {
      // Update inputs state with empty value
      setInputs({
        ...inputs,
        [key]: newValue,
      });
      return;
    }

    // Validate input based on field type and key
    let validationError = "";

    // Character limit validation
    if (type === 'text' && newValue.length > 25) {
      validationError = "Input cannot exceed 25 characters";
    }
    // Special character validation based on field type
    else if (key === "title") {
      if(containsSpecialCharactersForTitle(newValue))
        validationError = "Input cannot include special characters except '-', '_', or '/'";
    }
    else if (key === "vendorName") {
      if(checkAlphaNumeric(newValue))
        validationError = "Special characters are not allowed";
    }
    else if (newValue && containsSpecialCharacters(newValue)) {
      validationError = "Special characters are not allowed";
    }

    // Update error state if validation failed
    if (validationError) {
      setErrors(prev => ({ ...prev, [key]: validationError }));
    }

    // Update inputs state regardless of validation result
    setInputs({
      ...inputs,
      [key]: newValue,
    });
  };

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={() =>
        setIsOpen({
          isOpen: false,
        })
      }
      aria-label="Upload Dialog"
      className="max-h-[calc(100vh-96px)] overflow-y-auto"
    >
      <ZModalContent className="max-w-lg">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-4">
          <p>{helperData?.title}</p>
          <p className="text-sm text-center font-normal text-[#777780]">
            {helperData?.desc}
          </p>
        </ZModalHeader>
        <ZModalBody className="pt-0 overflow-auto">
          <div className="grid gap-4 py-0">
            <div className="grid w-full items-center gap-4">
              {helperData?.inputs?.map(
                (
                  input: {
                    key: string;
                    label: string;
                    placeholder: string;
                    type: string;
                    isRequired: boolean;
                  },
                  index: number
                ) =>
                  input.type === "textarea" ? (
                    <Textarea
                      key={input.key}
                      label={input.label}
                      placeholder={input.placeholder}
                      value={
                        inputs[input.key] &&
                          typeof inputs[input.key] === "string"
                          ? (inputs[input.key] as string)
                          : ""
                      }
                      onChange={(e) => {
                        setErrors({ ...errors, [input.key]: "" });
                        setInputs({
                          ...inputs,
                          [input.key]: e.target.value.trimStart(),
                        });
                      }}
                      labelPlacement="outside"
                      errorMessage={errors[input.key]}
                      isInvalid={!!errors[input.key]}
                      variant="faded"
                      autoFocus={index === 0}
                      classNames={{
                        inputWrapper: cn(
                          "w-full shadow-none  rounded-md border-1 border-gray-300",
                          !!errors[input.key] && "border-[#F31260]"
                        ),
                      }}
                    />
                  ) : input.type === "toggle" ? (
                    <div className="py-4" key={input.key}>
                      <Switch
                        isSelected={
                          inputs[input.key] &&
                            typeof inputs[input.key] === "boolean"
                            ? (inputs[input.key] as boolean)
                            : false
                        }
                        onValueChange={(isSelected) => {
                          setInputs({
                            ...inputs,
                            [input.key]: isSelected,
                          });
                        }}
                        size="sm"
                      >
                        <div className="pl-1 font-medium text-sm">
                          {(helperData as any)?.toggle?.label}
                        </div>
                      </Switch>
                    </div>
                  ) : (
                    <ZInput
                      key={input.key}
                      label={input.label}
                      placeholder={input.placeholder}
                      type={input.type}
                      value={
                        inputs[input.key] &&
                          typeof inputs[input.key] === "string"
                          ? (inputs[input.key] as string)
                          : ""
                      }
                      onChange={(e) => handleInputChange(e, input.key, input.type)}
                      labelPlacement="outside"
                      maxLength={25}
                      errorMessage={errors[input.key]}
                      isInvalid={!!errors[input.key]}
                      variant="faded"
                      autoFocus={index === 0}
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm  shadow-none rounded-md"
                      classNames={{
                        inputWrapper: cn(
                          "w-full shadow-none  rounded-md border-1 border-gray-300",
                          !!errors[input.key] && "border-[#F31260]"
                        ),
                      }}
                    />
                  )
              )}
            </div>
            {helperData?.dropdown ? (
              <Select
                classNames={{
                  mainWrapper: cn(
                    "w-full shadow-none  rounded-md border-1 border-gray-300",
                    !!errors.session && "border-[#F31260]"
                  ),
                  popoverContent: cn(
                    "w-full shadow-none  rounded-md border-1 border-gray-300",
                    !!errors.session && "border-[#F31260]"
                  ),
                }}
                items={filteredDropdownOptions}
                label={helperData.dropdown.label}
                labelPlacement="outside"
                value={selectedDropdownOption}
                placeholder={helperData.dropdown.placeholder}
                onChange={(value) => {
                  setSelectedDropdownOption(value.target.value);
                }}
                selectedKeys={selectedDropdownOption ? new Set([selectedDropdownOption]) : new Set()}
                disabledKeys={selectedDropdownOption ? new Set([selectedDropdownOption]) : new Set()}
              >
                {(item: { value: string; label: string }) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            ) : null}
            {helperData &&
              selectedDropdownOption &&
              selectedDropdownOption.includes(GapAssessmentTypes.SOC2) &&
              (helperData as any)?.toggle && (
                <div className="py-4 ">
                  <Switch
                    isSelected={isToggleSelected}
                    onValueChange={setIsToggleSelected}
                    size="sm"
                  >
                    <div className="pl-1 font-medium text-sm">
                      {(helperData as any)?.toggle?.label}
                    </div>
                  </Switch>
                </div>
              )}
            {selectedDropdownOption === RiskAssessmentTypes.FAIR && (
              <div className="grid w-full items-center gap-1.5">
                <Textarea
                  label="Risk to be evaluated"
                  placeholder="Add risk"
                  value={inputs.risk_statement as string || ""}
                  onChange={(e) => {
                    setErrors({ ...errors, risk: "" });
                    setInputs({
                      ...inputs,
                      risk_statement: e.target.value.trimStart(),
                    });
                  }}
                  labelPlacement="outside"
                  errorMessage={errors.risk}
                  isInvalid={!!errors.risk}
                  variant="faded"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.risk && "border-[#F31260]"
                    ),
                  }}
                />
              </div>
            )}
            {helperData?.uploads?.map?.(
              ({
                title,
                allowedFileTypes,
                type,
                maxFiles,
              }: {
                title: string;
                allowedFileTypes: string[];
                type: string;
                maxFiles: number;
              }) => (
                <div className="grid w-full items-center gap-1.5" key={title}>
                  <p className="text-small">
                    {selectedDropdownOption === RiskAssessmentTypes.FAIR
                      ? "Upload Documents (Optional)"
                      : title}
                  </p>
                  <FileUpload
                    maxFiles={selectedDropdownOption === RiskAssessmentTypes.FAIR ? 5 : maxFiles}
                    isMultiple={true}
                    allowedFileTypes={allowedFileTypes}
                    setFiles={(files: UploadedFile[], type: string) =>
                      handleSetFiles(files, type)
                    }
                    type={type}
                    ref={(el: HTMLDivElement) =>
                      (fileUploadRefs.current[type] = el)
                    }
                    key={`${type}-${selectedDropdownOption || 'default'}`}
                  />
                </div>
              )
            )}
          </div>
        </ZModalBody>
        <ZModalFooter>
          <ZButton
            variant="bordered"
            onClick={() =>
              setIsOpen({
                isOpen: false,
              })
            }
            className="px-3 py-1 h-auto text-sm"
          >
            Cancel
          </ZButton>
          <ZButton
            type="submit"
            onClick={() => {
              handleSubmit().catch(e => {
                console.error(e);
                return;
              });
            }}
            isLoading={isLoading}
            isDisabled={isLoading || buttonDisabled}
            disabled={isLoading || buttonDisabled}
            className="px-3 py-1 h-auto text-sm"
          >
            Submit
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
}
